import React, { createContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "";
const SUBDOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const RESERVED = new Set([
  "www",
  "api",
  "app",
  "admin",
  "static",
  "widget",
  "assets",
  "cdn",
  "docs",
  "help",
  "support",
  "mail",
  "smtp",
  "ftp",
  "vercel",
  "localhost",
]);

// Tenant-subdomain redirects are production-only behavior. On preview/dev
// deployments they bounce users to the prod tenant host, so they can be turned
// off per deployment by setting REACT_APP_DISABLE_TENANT_REDIRECT=true.
// Defaults to enabled so a missing or misconfigured var never breaks prod.
export const TENANT_REDIRECT_DISABLED =
  process.env.REACT_APP_DISABLE_TENANT_REDIRECT === "true";

/**
 * Parse the current hostname into a tenant subdomain slug, or null if the
 * page is on the apex domain (or a reserved/non-tenant host).
 *
 * Examples:
 *   tdw.addflint.io          -> "tdw"
 *   www.tdw.addflint.io      -> "tdw"  (leading "www" is stripped)
 *   www.addflint.io          -> null   (apex with www, no tenant)
 *   addflint.io              -> null
 *   tdw.appreciu.io          -> "tdw"
 *   tdw.localhost            -> "tdw"  (dev convenience)
 *   localhost / 127.0.0.1    -> null
 */
export function getSubdomain(hostname = window.location.hostname) {
  if (!hostname) return null;
  // Strip port if present
  const host = hostname.split(":")[0].toLowerCase();
  if (host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return null;

  let parts = host.split(".");
  // Treat a leading "www" as a no-op DNS prefix when it's not the only
  // label before the apex. www.tdw.addflint.io behaves like tdw.addflint.io;
  // www.addflint.io is the bare apex (handled below by the reserved check).
  if (parts.length >= 4 && parts[0] === "www") {
    parts = parts.slice(1);
  }
  if (parts.length < 2) return null;

  // For dev: tdw.localhost -> "tdw"
  if (parts.length === 2 && parts[1] === "localhost") {
    const slug = parts[0];
    return RESERVED.has(slug) || !SUBDOMAIN_RE.test(slug) ? null : slug;
  }

  // For prod: tdw.addflint.io -> "tdw" (3+ parts)
  if (parts.length >= 3) {
    const slug = parts[0];
    if (RESERVED.has(slug) || !SUBDOMAIN_RE.test(slug)) return null;
    return slug;
  }

  return null;
}

/**
 * Build the apex (registrable) host for the current location, e.g.
 *   tdw.addflint.io -> addflint.io
 *   addflint.io     -> addflint.io
 *   tdw.localhost   -> localhost
 */
export function getApexHost(hostname = window.location.hostname) {
  const host = hostname.split(":")[0].toLowerCase();
  if (host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return host;
  const parts = host.split(".");
  if (parts.length === 2 && parts[1] === "localhost") return "localhost";
  if (parts.length >= 2) return parts.slice(-2).join(".");
  return host;
}

const TenantContext = createContext({
  subdomain: null,
  scopedOrgId: null,
  scopedTheme: null,
  loading: true,
});

export const TenantProvider = ({ children }) => {
  const subdomain = useMemo(() => getSubdomain(), []);
  const [scopedOrgId, setScopedOrgId] = useState(null);
  const [scopedTheme, setScopedTheme] = useState(null);
  const [loading, setLoading] = useState(!!subdomain);

  useEffect(() => {
    let cancelled = false;
    if (!subdomain) {
      setLoading(false);
      return undefined;
    }
    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/v1/org/by-subdomain/${encodeURIComponent(subdomain)}`,
        );
        if (cancelled) return;
        setScopedOrgId(res.data?.organization_id || null);
        setScopedTheme(res.data || null);
      } catch {
        if (cancelled) return;
        // Subdomain not bound to an org — treat as apex.
        setScopedOrgId(null);
        setScopedTheme(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [subdomain]);

  const value = useMemo(
    () => ({ subdomain, scopedOrgId, scopedTheme, loading }),
    [subdomain, scopedOrgId, scopedTheme, loading],
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};

export default TenantContext;
